import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { supabase } from '../../supabase';
import {
  createMarketPriceCaptureIntent,
  completeMarketPriceCapture,
  type MarketPriceCaptureIntent,
} from '../../api/market-price-capture';
import { getMarkets } from '../../api/markets';
import { getShipmentPriceBoxes } from '../../api/shipment-price-boxes';
import { getCurrencies } from '../../api/currencies';
import { BoxFormatMapping, Market, Shipment } from '../../utils/types';
import { useTranslation } from '../../hooks/useTranslation';
import useUserStore from '../../stores/useUserStore';
import { canLogPricesWithoutContainer } from '../../utils/inbox-access';
import { Alert } from '../ui';
import PackingCodeMappingEditor from '../shipments/PackingCodeMappingEditor';
import LogbookNoteEditor from '../shipments/LogbookNoteEditor';

/**
 * Flujo rápido para registrar a qué precio se vendió un contenedor, pensado
 * para alguien parado en el mercado con el celular.
 *
 * Con contenedor, el precio es por caja: cada caja del despacho guarda su
 * precio informado (shipment_boxes.reported_*). El nivel de detalle que elige
 * el usuario (variedad, calibre, formato, peso, color, etiqueta, folio) solo
 * define cómo se agrupan las cajas en pantalla: un precio puesto a un grupo se
 * copia a todas sus cajas. Por eso cambiar el nivel no pierde lo escrito, y al
 * volver al contenedor otro día aparecen los precios ya informados.
 *
 * Además, cada producto con precio nuevo queda como registro de Bitácora
 * (market_comments), que es de donde salen los puntos morados.
 */

interface ShipmentOption {
  id: number;
  // Los aéreos no tienen contenedor: se identifican por la referencia.
  container_number?: string | null;
  receiver?: string | null;
  ship_name?: string | null;
  ship_eta?: string | null;
  reference_number?: string | null;
  ship_destination_country?: string | null;
  ship_destination_port?: string | null;
}

/** Una fila de shipment_boxes, ya normalizada para agrupar. */
interface BoxRow {
  id: number;
  boxType: string | null;
  variety: string;
  caliber: string;
  format: string | null;
  weight: number | null;
  color: string | null;
  category: string | null;
  label: string | null;
  pallet: string | null;
  quantity: number;
  reportedPrice: number | null;
  reportedCurrency: string | null;
  reportedSaleDate: string | null;
}

type Dimension =
  | 'variety'
  | 'caliber'
  | 'format'
  | 'weight'
  | 'color'
  | 'category'
  | 'label'
  | 'pallet';

/**
 * Registro sin contenedor: variedad, calibre y peso son obligatorios (los
 * pide el gráfico); formato, color, etiqueta y categoría se piden si se
 * eligen en "Abrir por".
 */
interface ManualRow {
  key: string;
  variety: string;
  caliber: string;
  weight: string;
  price: string;
  format: string;
  color: string;
  label: string;
  category: string;
}

export interface RegisterPricesFlowProps {
  isOpen: boolean;
  onClose: () => void;
  /** Refresca datos persistidos, incluso si todavía quedan fotos pendientes. */
  onSaved?: () => void;
  exporterId?: number;
  userId?: number;
  userName?: string;
  speciesId?: number;
  season?: string;
  varieties?: string[];
  calibers?: string[];
  defaultMarket?: string;
  presentation?: 'modal' | 'embedded';
  initialShipment?: Shipment;
  onBusyChange?: (busy: boolean) => void;
  onFormatMapped?: (mapping: BoxFormatMapping) => void;
}

// Mientras cargan las monedas de la tabla currencies.
const BASE_CURRENCIES = ['RMB', 'USD'];

/** "SHENZHEN / CHINA", "MANZANILLO, MX" → "SHENZHEN", "MANZANILLO". */
const portName = (port: string | null) =>
  (port || '').split(/[/,]/)[0].trim().toUpperCase();

// País y puerto de destino vienen como texto libre ("CN", "SHENZHEN / CHINA",
// o el puerto en la columna del país), así que se busca por palabras. Solo se
// usa si no se pudo sugerir un mercado.
const CHINA_DESTINATION =
  /\b(CHINA|CN|HONG KONG|SHENZHEN|NANSHA|SHANGHAI|PUDONG|GUANGZHOU|BAIYUN|HUANGPU|YANTIAN|SHEKOU|DA CHAN BAY|BEIJING|TIANJIN|QINGDAO|NINGBO|XIAMEN|DALIAN|CHENGDU|CHONGQING|EZHOU|HEFEI|ZHENGZHOU|WUHAN|KUNMING)\b/;

/**
 * Moneda por defecto según el puerto donde se descargó la fruta: RMB si se
 * descargó en China (o Hong Kong), USD en otro destino. Sin destino, RMB.
 */
const currencyForDestination = (option: ShipmentOption) => {
  const destination = [
    option.ship_destination_country,
    option.ship_destination_port,
  ]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
  if (!destination.trim() || CHINA_DESTINATION.test(destination)) return 'RMB';
  return 'USD';
};
const SEARCH_MIN_CHARS = 3;

// Orden en que se muestran y concatenan los niveles de detalle.
const DIMENSIONS: Dimension[] = [
  'variety',
  'caliber',
  'format',
  'weight',
  'color',
  'category',
  'label',
  'pallet',
];
// Por defecto: la unidad mínima de precio. El peso es clave, porque un mismo
// Lapins 2J puede venir en cajas de 2,5 y de 5 kg.
const DEFAULT_DIMENSIONS: Dimension[] = [
  'variety',
  'caliber',
  'format',
  'weight',
];

/** El valor si todos coinciden; si hay varios (o ninguno), null. */
const onlyValue = (values: Set<string>) =>
  values.size === 1 ? Array.from(values)[0] : null;

// Sin contenedor no hay folios; variedad, calibre y peso no se pueden quitar.
const MANUAL_DIMENSIONS = DIMENSIONS.filter((d) => d !== 'pallet');
const REQUIRED_MANUAL_DIMENSIONS: Dimension[] = [
  'variety',
  'caliber',
  'weight',
];

const today = () => new Date().toISOString().split('T')[0];
const newKey = () => Math.random().toString(36).slice(2, 10);
const emptyManualRow = (): ManualRow => ({
  key: newKey(),
  variety: '',
  caliber: '',
  weight: '',
  price: '',
  format: '',
  color: '',
  label: '',
  category: '',
});

const toNumber = (value: string) => {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

const normalizeCode = (value: string | null | undefined) =>
  (value || '').trim().toUpperCase();

const normalizeBoxFormat = (value: string | null | undefined) => {
  const format = (value || '').trim();
  return ['', 'NA', 'N/A', 'NO ENCONTRADO'].includes(normalizeCode(format))
    ? null
    : format;
};

const formatWeight = (kg: number | null) =>
  kg == null ? null : `${String(kg).replace('.', ',')} kg`;

const daysBetween = (from: string | null, to: string | null) => {
  if (!from || !to) return null;
  const ms =
    new Date(`${to}T12:00:00Z`).getTime() -
    new Date(`${from}T12:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
};

/**
 * Los contenedores llegan con dos formatos ("FBIU 567080-5" y
 * "FBIU569130-4"). Se busca el prefijo con y sin espacio entre las letras y
 * los números, para que escribir "FBIU56" encuentre los dos.
 */
const containerPatterns = (raw: string) => {
  const clean = raw.toUpperCase().replace(/[\s-]/g, '');
  const match = clean.match(/^([A-Z]+)(\d*)$/);
  if (!match || !match[2]) return [`${clean}%`];
  const [, letters, digits] = match;
  return [`${letters}${digits}%`, `${letters} ${digits}%`];
};

export default function RegisterPricesFlow(props: RegisterPricesFlowProps) {
  const contextKey = JSON.stringify([
    props.isOpen,
    props.exporterId,
    props.userId,
    props.speciesId,
    props.season,
    props.initialShipment?.id,
    props.initialShipment?.reference_number,
    props.initialShipment?.container_number,
    props.initialShipment?.receiver,
    props.initialShipment?.ship_name,
    props.initialShipment?.ship_eta,
    props.initialShipment?.ship_destination_country,
    props.initialShipment?.ship_destination_port,
  ]);
  return <RegisterPricesSession key={contextKey} {...props} />;
}

function RegisterPricesSession({
  isOpen,
  onClose,
  onSaved,
  exporterId,
  userId,
  userName,
  speciesId,
  season,
  varieties = [],
  calibers = [],
  defaultMarket,
  presentation = 'modal',
  initialShipment,
  onBusyChange,
  onFormatMapped,
}: RegisterPricesFlowProps) {
  const { t } = useTranslation('market');
  const { userProfile } = useUserStore();
  const canLogWithoutContainer = canLogPricesWithoutContainer(userProfile);

  const DIMENSION_LABELS: Record<Dimension, string> = {
    variety: t('Variedad'),
    caliber: t('Calibre'),
    format: t('Formato'),
    weight: t('Peso'),
    color: t('Color'),
    category: t('Categoría'),
    label: t('Etiqueta'),
    pallet: t('Folio'),
  };

  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ShipmentOption[]>([]);
  // Texto al que corresponden `results`: el mensaje "no hay" solo se muestra
  // cuando la búsqueda del texto escrito ya terminó.
  const [searchedTerm, setSearchedTerm] = useState('');
  const [shipment, setShipment] = useState<ShipmentOption | null>(
    initialShipment ?? null
  );
  const [withoutContainer, setWithoutContainer] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(
    Boolean(initialShipment)
  );
  const [productsError, setProductsError] = useState(false);
  const [showFormatMapping, setShowFormatMapping] = useState(false);

  // Con contenedor
  const [boxes, setBoxes] = useState<BoxRow[]>([]);
  const [dimensions, setDimensions] = useState<Dimension[]>(DEFAULT_DIMENSIONS);
  const [manualDimensions, setManualDimensions] =
    useState<Dimension[]>(DEFAULT_DIMENSIONS);
  const [boxPrices, setBoxPrices] = useState<Record<number, string>>({});
  const [editedBoxIds, setEditedBoxIds] = useState<Set<number>>(new Set());
  // Sin contenedor
  const [manualRows, setManualRows] = useState<ManualRow[]>([]);

  const [saleDate, setSaleDate] = useState(today());
  const [market, setMarket] = useState(defaultMarket ?? '');
  // De dónde salió el mercado sugerido, para explicarlo bajo el selector.
  const [marketSource, setMarketSource] = useState<'tracking' | 'port' | null>(
    null
  );
  const [currency, setCurrency] = useState('RMB');
  const [currencyOptions, setCurrencyOptions] =
    useState<string[]>(BASE_CURRENCIES);
  const [comment, setComment] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [saving, setSaving] = useState(false);
  const [capturePending, setCapturePending] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const captureIntentRef = useRef<MarketPriceCaptureIntent | null>(null);
  const draftLocked = saving || capturePending;
  const [showErrors, setShowErrors] = useState(false);
  const firstStep = initialShipment ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const mounted = useRef(false);
  const productRequestVersion = useRef(0);
  const saveRequestRef = useRef<object | null>(null);
  const initialLoadRef = useRef<() => void>(() => {});
  const busyCallbackRef = useRef(onBusyChange);
  busyCallbackRef.current = onBusyChange;
  const onFormatMappedRef = useRef(onFormatMapped);
  onFormatMappedRef.current = onFormatMapped;
  const marketsRequestRef = useRef<Promise<Market[]> | null>(null);
  const loadMarkets = useCallback(() => {
    if (!marketsRequestRef.current) {
      marketsRequestRef.current = getMarkets()
        .then((list) =>
          [...list].sort(
            (a, b) =>
              Number(b.country_code === 'CN' || b.country === 'China') -
                Number(a.country_code === 'CN' || a.country === 'China') ||
              a.name.localeCompare(b.name)
          )
        )
        .catch((error) => {
          marketsRequestRef.current = null;
          throw error;
        });
    }
    return marketsRequestRef.current;
  }, []);

  useEffect(() => {
    mounted.current = true;
    initialLoadRef.current();
    return () => {
      mounted.current = false;
      productRequestVersion.current += 1;
      saveRequestRef.current = null;
      busyCallbackRef.current?.(false);
    };
  }, []);

  // En el celular el modal ocupa toda la pantalla. NextUI no respeta clases
  // responsivas sobre su contenedor, así que se cambia el tamaño.
  const [isSmallScreen, setIsSmallScreen] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 639px)').matches
  );
  useEffect(() => {
    const media = window.matchMedia('(max-width: 639px)');
    const onChange = (e: MediaQueryListEvent) => setIsSmallScreen(e.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const reset = () => {
    captureIntentRef.current = null;
    setCapturePending(false);
    setCaptureError(null);
    setLoadingProducts(false);
    setProductsError(false);
    setShowFormatMapping(false);
    setSearch('');
    setResults([]);
    setSearchedTerm('');
    setShipment(initialShipment ?? null);
    setWithoutContainer(false);
    setBoxes([]);
    setDimensions(DEFAULT_DIMENSIONS);
    setManualDimensions(DEFAULT_DIMENSIONS);
    setBoxPrices({});
    setEditedBoxIds(new Set());
    setManualRows([]);
    setSaleDate(today());
    setMarket(defaultMarket ?? '');
    setMarketSource(null);
    setCurrency('RMB');
    setComment('');
    setPhotos([]);
    setShowErrors(false);
    setStep(firstStep);
  };

  const handleClose = () => {
    if (saveRequestRef.current) return;
    if (
      captureIntentRef.current?.uncertainCommit &&
      captureIntentRef.current.commentIds === null
    )
      onSaved?.();
    productRequestVersion.current += 1;
    reset();
    onClose();
  };

  // Share the catalog request with an immediately selected shipment.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    loadMarkets()
      .then((list) => {
        if (active) setMarkets(list);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isOpen, loadMarkets]);

  useEffect(() => {
    if (!isOpen || currencyOptions !== BASE_CURRENCIES) return;
    let active = true;
    getCurrencies()
      .then((list) => {
        if (!active) return;
        const values = list.map((c) => c.value);
        // RMB y USD primero: son las de los gráficos de Mercado.
        setCurrencyOptions([
          ...BASE_CURRENCIES,
          ...values.filter((v) => !BASE_CURRENCIES.includes(v)),
        ]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [isOpen, currencyOptions]);

  // Contenedor, o la referencia si es un aéreo.
  const shipmentLabel = (option: ShipmentOption) =>
    option.container_number || `${t('Ref.')} ${option.reference_number || '-'}`;
  // Si se encontró por referencia (y no por contenedor), se muestra cuál.
  const matchedByReference = (option: ShipmentOption) =>
    !!option.container_number &&
    !!option.reference_number &&
    option.reference_number
      .toUpperCase()
      .startsWith(searchedTerm.toUpperCase()) &&
    !option.container_number
      .replace(/[\s-]/g, '')
      .startsWith(searchedTerm.toUpperCase().replace(/[\s-]/g, ''));

  const marketById = useMemo(
    () => new Map(markets.map((m) => [m.id, m])),
    [markets]
  );
  // Los alias (con mercado primario) no se ofrecen: se usa su primario.
  const marketOptions = useMemo(
    () => markets.filter((m) => !m.primary_market_id),
    [markets]
  );
  const currencyOfMarket = (id: string) =>
    marketById.get(id)?.default_currency || null;

  const chooseMarket = (id: string) => {
    setMarket(id);
    setMarketSource(null);
    const marketCurrency = currencyOfMarket(id);
    if (marketCurrency) setCurrency(marketCurrency);
  };

  /**
   * Mercado del contenedor: el de destino del tracking; si no está trackeado,
   * el mercado que se llama igual que el puerto de descarga del despacho.
   */
  const suggestMarket = async (
    option: ShipmentOption,
    catalog: Market[] = markets
  ): Promise<{ id: string; source: 'tracking' | 'port' } | null> => {
    const primaryId = (id: string) => {
      const item = catalog.find((m) => m.id === id);
      return item?.effective_market_id || item?.primary_market_id || id;
    };
    if (option.container_number) {
      const { data: tracked } = await supabase.rpc(
        'get_container_destination_market',
        {
          p_container_number: option.container_number,
        }
      );
      if (tracked)
        return { id: primaryId(tracked as string), source: 'tracking' };
    }

    const port = portName(option.ship_destination_port ?? null);
    const byPort = port
      ? catalog.find((m) => m.name.toUpperCase() === port)
      : undefined;
    if (byPort) return { id: primaryId(byPort.id), source: 'port' };
    return null;
  };

  // 1. Buscar contenedor por prefijo, solo entre los despachos propios.
  useEffect(() => {
    if (!isOpen || initialShipment || shipment || withoutContainer) return;
    const term = search.trim();
    if (term.replace(/[\s-]/g, '').length < SEARCH_MIN_CHARS || !exporterId) {
      setResults([]);
      setSearchedTerm('');
      setSearching(false);
      return;
    }

    // Se marca como cargando ya, no cuando sale la consulta: si no, durante
    // la espera se ve "no hay contenedores" y después aparecen los resultados.
    setSearching(true);
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      // Contenedor o referencia de la exportadora (los aéreos no tienen
      // contenedor). Las comillas protegen el texto dentro del filtro.
      const reference = term.replace(/["\\]/g, '');
      const filters = [
        ...containerPatterns(term).map((p) => `container_number.ilike.${p}`),
        `reference_number.ilike."${reference}%"`,
      ].join(',');
      let query = supabase
        .from('shipments')
        .select(
          'id, container_number, receiver, ship_name, ship_eta, reference_number, ship_destination_country, ship_destination_port'
        )
        .is('source_deleted_at', null)
        .eq('exporter_id', exporterId)
        .or(filters)
        .order('ship_eta', { ascending: false, nullsFirst: false })
        .limit(8);
      if (speciesId) query = query.eq('species_id', speciesId);
      if (season) query = query.eq('season', season);

      const { data, error } = await query;
      if (cancelled) return;
      if (error) {
        console.error('Error buscando contenedores:', error);
        setResults([]);
      } else {
        setResults((data as ShipmentOption[]) || []);
      }
      setSearchedTerm(term);
      setSearching(false);
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    search,
    shipment,
    withoutContainer,
    exporterId,
    speciesId,
    season,
    isOpen,
    initialShipment,
  ]);

  // 2. Al elegir contenedor, traer sus cajas y los precios ya informados.
  const selectShipment = async (option: ShipmentOption) => {
    const requestVersion = ++productRequestVersion.current;
    const isCurrent = () =>
      mounted.current && productRequestVersion.current === requestVersion;
    setShowFormatMapping(false);
    setShipment(option);
    setResults([]);
    setStep(1);
    setBoxes([]);
    setBoxPrices({});
    setEditedBoxIds(new Set());
    setProductsError(false);
    setLoadingProducts(true);
    try {
      if (!exporterId) throw new Error('Missing exporter');
      const [priceBoxes, formatsRes, catalog] = await Promise.all([
        getShipmentPriceBoxes(option.id, exporterId, season, speciesId),
        supabase
          .from('frutic_box_to_format')
          .select('frutic_code, cherry_box_format')
          .eq('exporter_id', exporterId),
        loadMarkets(),
      ]);
      if (!isCurrent()) return;
      setMarkets(catalog);
      // El formato no viene en la caja: se arma con el mapeo de códigos de caja
      // de la exportadora, igual que en Despachos.
      const formatByCode = new Map<string, string>();
      for (const m of formatsRes.data || []) {
        if (m.frutic_code)
          formatByCode.set(normalizeCode(m.frutic_code), m.cherry_box_format);
      }

      const rows: BoxRow[] = priceBoxes.map((b) => {
        const mapped = formatByCode.get(normalizeCode(b.box_type));
        return {
          id: b.id,
          boxType: b.box_type?.trim() || null,
          // "Variedad" es la variedad etiquetada; si falta, la variedad a secas.
          variety: (b.labeled_variety || b.variety || '').trim(),
          caliber: (b.caliber || '').trim(),
          format:
            normalizeBoxFormat(mapped) || normalizeBoxFormat(b.box_format),
          weight: b.box_weight != null ? Number(b.box_weight) : null,
          color: b.color?.trim() || null,
          category: b.category?.trim() || null,
          label: b.box_label?.trim() || null,
          pallet: b.pallet?.trim() || null,
          quantity: b.quantity || 0,
          reportedPrice:
            b.reported_price != null ? Number(b.reported_price) : null,
          reportedCurrency: b.reported_price_currency || null,
          reportedSaleDate: b.reported_sale_date || null,
        };
      });

      // Mercado: el del tracking o el del puerto de descarga; si no, el del
      // filtro de la página. Moneda: la de los precios ya informados; si no
      // hay, la del mercado; y si el mercado no la tiene, según el destino.
      const suggested = await suggestMarket(option, catalog);
      if (!isCurrent()) return;
      const marketId = suggested?.id || defaultMarket || '';
      setMarket(marketId);
      setMarketSource(suggested?.source ?? null);
      const reportedCurrency = rows.find(
        (b) => b.reportedPrice != null && b.reportedCurrency
      )?.reportedCurrency;
      setCurrency(
        reportedCurrency ||
          catalog.find((m) => m.id === marketId)?.default_currency ||
          currencyForDestination(option)
      );

      setBoxes(rows);
      setBoxPrices(
        Object.fromEntries(
          rows
            .filter((b) => b.reportedPrice != null)
            .map((b) => [b.id, String(b.reportedPrice)])
        )
      );
      setEditedBoxIds(new Set());
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error cargando productos del despacho:', error);
      setProductsError(true);
    } finally {
      if (isCurrent()) setLoadingProducts(false);
    }
  };
  initialLoadRef.current = () => {
    if (isOpen && initialShipment) void selectShipment(initialShipment);
  };

  const chooseWithoutContainer = () => {
    if (initialShipment || saveRequestRef.current) return;
    productRequestVersion.current += 1;
    setLoadingProducts(false);
    setProductsError(false);
    setWithoutContainer(true);
    setCurrency(currencyOfMarket(market) || 'RMB');
    setShipment(null);
    setResults([]);
    setManualRows([emptyManualRow()]);
    setStep(1);
  };

  const changeContainer = () => {
    if (initialShipment || saveRequestRef.current) return;
    productRequestVersion.current += 1;
    setLoadingProducts(false);
    setProductsError(false);
    setShipment(null);
    setWithoutContainer(false);
    setBoxes([]);
    setBoxPrices({});
    setEditedBoxIds(new Set());
    setManualRows([]);
    setSearch('');
    setStep(0);
  };

  // Categoría solo si el contenedor trae más de una: en cereza casi siempre
  // es la misma (CAT 1) y no aporta; en pera o uva sí separa precios.
  const visibleDimensions = useMemo(() => {
    const categories = new Set(boxes.map((b) => b.category).filter(Boolean));
    return DIMENSIONS.filter((d) => d !== 'category' || categories.size > 1);
  }, [boxes]);

  const toggleIn = (prev: Dimension[], dimension: Dimension) =>
    prev.includes(dimension)
      ? prev.filter((d) => d !== dimension)
      : DIMENSIONS.filter((d) => d === dimension || prev.includes(d));

  const toggleDimension = (dimension: Dimension) =>
    setDimensions((prev) => toggleIn(prev, dimension));

  const toggleManualDimension = (dimension: Dimension) => {
    if (REQUIRED_MANUAL_DIMENSIONS.includes(dimension)) return;
    setManualDimensions((prev) => toggleIn(prev, dimension));
  };

  const dimensionValue = (box: BoxRow, dimension: Dimension) => {
    switch (dimension) {
      case 'weight':
        return formatWeight(box.weight);
      case 'pallet':
        return box.pallet ? `${t('Folio')} ${box.pallet}` : null;
      default:
        return box[dimension];
    }
  };

  // Grupos según el nivel de detalle. El precio de un grupo es el de sus
  // cajas si todas coinciden; si no, queda "varios precios".
  const groups = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        parts: (string | null)[];
        boxIds: number[];
        quantity: number;
      }
    >();
    for (const box of boxes) {
      const parts = dimensions.map((d) => dimensionValue(box, d));
      const key = parts.map((p) => p ?? '—').join('|');
      const group = map.get(key) || { key, parts, boxIds: [], quantity: 0 };
      group.boxIds.push(box.id);
      group.quantity += box.quantity;
      map.set(key, group);
    }
    return Array.from(map.values())
      .map((group) => {
        const prices = new Set(group.boxIds.map((id) => boxPrices[id] ?? ''));
        // Si las cajas tienen precios distintos, el rango (sin contar vacías).
        const numeric = Array.from(prices)
          .map((p) => toNumber(p))
          .filter((n) => n > 0);
        const range =
          prices.size > 1 && numeric.length > 0
            ? `${Math.min(...numeric)}–${Math.max(...numeric)}`
            : null;
        const members = boxes.filter((b) => group.boxIds.includes(b.id));
        const pallets = new Set(members.map((b) => b.pallet).filter(Boolean));
        const reported = members.filter((b) => b.reportedPrice != null);
        const reportedDates = new Set(reported.map((b) => b.reportedSaleDate));
        return {
          ...group,
          price: prices.size === 1 ? Array.from(prices)[0] : null,
          range,
          pallets: pallets.size,
          edited: group.boxIds.some((id) => editedBoxIds.has(id)),
          // Lo ya informado en otra visita, para mostrarlo en la fila.
          reportedAll:
            reported.length === members.length && reported.length > 0,
          reportedSome: reported.length > 0,
          reportedDate:
            reportedDates.size === 1 ? Array.from(reportedDates)[0] : null,
        };
      })
      .sort((a, b) => b.quantity - a.quantity);
    // dimensionValue depende solo de `t`, estable entre renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, dimensions, boxPrices, editedBoxIds]);

  const setGroupPrice = (boxIds: number[], value: string) => {
    setBoxPrices((prev) => {
      const next = { ...prev };
      for (const id of boxIds) next[id] = value;
      return next;
    });
    setEditedBoxIds((prev) => new Set([...prev, ...boxIds]));
  };

  const updateManualRow = (key: string, patch: Partial<ManualRow>) =>
    setManualRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r))
    );

  const removeManualRow = (key: string) =>
    setManualRows((prev) => prev.filter((r) => r.key !== key));

  const priceError = (value: string) =>
    value.trim() && !(toNumber(value) > 0) ? t('Precio inválido') : undefined;

  const manualRowErrors = (row: ManualRow) => {
    const errors: Partial<Record<keyof ManualRow, string>> = {};
    if (!row.price.trim()) return errors;
    if (!(toNumber(row.price) > 0)) errors.price = t('Precio inválido');
    if (!row.variety.trim()) errors.variety = t('Falta la variedad');
    if (!row.caliber.trim()) errors.caliber = t('Falta el calibre');
    if (!(toNumber(row.weight) > 0))
      errors.weight = t('El peso es obligatorio');
    return errors;
  };

  // Cajas cuyo precio se tocó en esta visita (con o sin precio: vaciar un
  // precio también es un cambio).
  const editedBoxes = useMemo(
    () => boxes.filter((b) => editedBoxIds.has(b.id)),
    [boxes, editedBoxIds]
  );
  const pricedManualRows = manualRows.filter((r) => r.price.trim());

  const hasChanges = shipment
    ? editedBoxes.length > 0
    : pricedManualRows.length > 0;

  // El formato sale del mapeo de códigos de caja de la exportadora. Si falta,
  // esas cajas no se separan por formato, y hay que avisar cómo arreglarlo.
  const boxesWithoutFormat = useMemo(
    () =>
      boxes.filter((b) => !b.format).reduce((sum, b) => sum + b.quantity, 0),
    [boxes]
  );
  const codesWithoutFormat = useMemo(
    () =>
      Array.from(
        new Set(
          boxes
            .filter((b) => !b.format)
            .map((b) => b.boxType)
            .filter((code): code is string => !!code)
        )
      ).sort(),
    [boxes]
  );
  const applyFormatMapping = (mapping: BoxFormatMapping) => {
    setBoxes((current) =>
      current.map((box) =>
        normalizeCode(box.boxType) === normalizeCode(mapping.frutic_code)
          ? { ...box, format: normalizeBoxFormat(mapping.cherry_box_format) }
          : box
      )
    );
    onFormatMappedRef.current?.(mapping);
  };

  const daysFromEta = shipment
    ? daysBetween(shipment.ship_eta ?? null, saleDate)
    : null;

  // Al dejar el paso de productos se valida; si hay error se queda ahí y
  // baja hasta el primer producto con problemas.
  const validateProducts = () => {
    setShowErrors(true);
    if (!hasChanges) {
      toast.warning(t('Ingresa el precio de al menos un producto'));
      return false;
    }
    const invalidKey = shipment
      ? groups.find((g) => g.edited && g.price && priceError(g.price))?.key
      : pricedManualRows.find((r) => Object.keys(manualRowErrors(r)).length)
          ?.key;
    if (invalidKey) {
      window.requestAnimationFrame(() =>
        document
          .querySelector(`[data-row-key="${CSS.escape(invalidKey)}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      );
      return false;
    }
    return true;
  };

  /**
   * Puntos de Bitácora por producto (variedad, calibre, peso) tocado en esta
   * visita, sea cual sea el nivel al que se informó: si se puso precio solo a
   * una etiqueta, cada producto de esa etiqueta queda con ese precio.
   *
   * Se recalcula con todas las cajas del producto vendidas en la fecha de
   * venta, no solo las editadas: si se corrige un folio, los demás folios del
   * producto conservan su precio. En la base, los registros de ese contenedor,
   * producto y fecha se reemplazan en vez de sumarse.
   *
   * `count` son las cajas del contenedor, que es lo que pondera el punto.
   */
  const bitacoraProducts = useMemo(() => {
    if (!shipment) return [];
    const productKey = (b: BoxRow) =>
      [b.variety, b.caliber, b.weight].join('|');
    const touched = new Set(
      editedBoxes.filter((b) => b.weight != null).map(productKey)
    );
    const products = new Map<
      string,
      {
        variety: string;
        caliber: string;
        weight: number;
        byPrice: Map<
          number,
          {
            count: number;
            labels: Set<string>;
            formats: Set<string>;
            categories: Set<string>;
          }
        >;
      }
    >();
    for (const box of boxes) {
      const key = productKey(box);
      if (!touched.has(key) || box.weight == null) continue;
      const product = products.get(key) || {
        variety: box.variety,
        caliber: box.caliber,
        weight: box.weight,
        byPrice: new Map(),
      };
      products.set(key, product);

      const raw = (boxPrices[box.id] ?? '').trim();
      // Fecha efectiva: las cajas editadas se venden en `saleDate`; las demás
      // mantienen la fecha con que se informaron.
      const date = editedBoxIds.has(box.id) ? saleDate : box.reportedSaleDate;
      if (!raw || date !== saleDate) continue;
      const price = toNumber(raw);
      const entry = product.byPrice.get(price) || {
        count: 0,
        labels: new Set<string>(),
        formats: new Set<string>(),
        categories: new Set<string>(),
      };
      entry.count += box.quantity;
      if (box.label) entry.labels.add(box.label);
      if (box.format) entry.formats.add(box.format);
      if (box.category) entry.categories.add(box.category);
      product.byPrice.set(price, entry);
    }
    return Array.from(products.values()).map((p) => ({
      variety: p.variety,
      caliber: p.caliber,
      weight: p.weight,
      entries: Array.from(p.byPrice.entries()).map(([price, e]) => ({
        price,
        count: e.count,
        label: onlyValue(e.labels),
        format: onlyValue(e.formats),
        category: onlyValue(e.categories),
      })),
    }));
  }, [shipment, boxes, editedBoxes, editedBoxIds, boxPrices, saleDate]);

  // Un campo opcional se guarda solo si está elegido en "Abrir por".
  const optionalField = (value: string, dimension: Dimension) =>
    manualDimensions.includes(dimension) ? value.trim() || null : null;

  // Sin contenedor: un registro por fila con precio.
  const manualEntries = useMemo(
    () =>
      pricedManualRows.map((r) => ({
        variety: r.variety.trim(),
        caliber: r.caliber.trim(),
        weight: toNumber(r.weight),
        price: toNumber(r.price),
        format: optionalField(r.format, 'format'),
        color: optionalField(r.color, 'color'),
        label: optionalField(r.label, 'label'),
        category: optionalField(r.category, 'category'),
      })),
    // optionalField depende solo de manualDimensions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pricedManualRows, manualDimensions]
  );

  const handleSave = async () => {
    if (saveRequestRef.current || loadingProducts || productsError) return;
    if (!exporterId || !userId) {
      toast.error(t('No se pudo identificar tu usuario'));
      return;
    }
    if (!captureIntentRef.current) {
      if (!validateProducts()) {
        setStep(1);
        return;
      }
      const byPrice = new Map<string, number[]>();
      for (const box of editedBoxes) {
        const raw = (boxPrices[box.id] ?? '').trim();
        const key = raw ? String(toNumber(raw)) : '';
        byPrice.set(key, [...(byPrice.get(key) || []), box.id]);
      }
      captureIntentRef.current = createMarketPriceCaptureIntent(
        {
          exporter_id: exporterId,
          shipment_id: shipment?.id ?? null,
          container_number: shipment?.container_number || null,
          exporter_ref: shipment?.reference_number || null,
          seller: shipment?.receiver || null,
          ship_name: shipment?.ship_name || null,
          sale_date: saleDate,
          species_id: speciesId ?? null,
          season: season ?? null,
          market_id: market || null,
          currency,
          content: comment.trim() || null,
          has_photos: photos.length > 0,
          items: shipment
            ? Array.from(byPrice.entries()).map(([price, box_ids]) => ({
                box_ids,
                price: price ? Number(price) : null,
                currency,
                sale_date: saleDate,
              }))
            : [],
          products: shipment ? bitacoraProducts : [],
          manual_entries: shipment ? [] : manualEntries,
        },
        photos,
        userId
      );
    }
    const intent = captureIntentRef.current;
    const request = {};
    saveRequestRef.current = request;
    const isCurrent = () =>
      mounted.current && saveRequestRef.current === request;
    setSaving(true);
    setCapturePending(true);
    setCaptureError(null);
    busyCallbackRef.current?.(true);
    try {
      await completeMarketPriceCapture(intent);
      if (!isCurrent()) return;
      captureIntentRef.current = null;
      setCapturePending(false);
      saveRequestRef.current = null;
      setSaving(false);
      busyCallbackRef.current?.(false);
      const saved = shipment
        ? groups.filter((g) => g.edited).length
        : manualEntries.length;
      toast.success(
        saved === 1
          ? t('Precio registrado')
          : `${saved} ${t('precios registrados')}`
      );
      onSaved?.();
      handleClose();
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error registrando precios:', error);
      const code = (error as { code?: string } | null)?.code;
      const message =
        code === '40001'
          ? t(
              'La captura cambió en otra sesión. Cierra y vuelve a abrir el registro.'
            )
          : code === '42501'
            ? t(
                'No tienes permiso para guardar estos precios. Pídele al administrador de tu exportadora que te lo habilite.'
              )
          : intent.commentIds !== null
            ? t(
                'La captura se guardó, pero faltan fotos. Reintenta para completarlas.'
              )
            : intent.uncertainCommit
              ? t(
                  'No pudimos confirmar el guardado. Reintenta; se usará la misma captura.'
                )
              : t(
                  'No se pudieron guardar los precios. Puedes corregirlos y reintentar.'
                );
      if (intent.commentIds === null && !intent.uncertainCommit) {
        captureIntentRef.current = null;
        setCapturePending(false);
      }
      setCaptureError(message);
      if (intent.commentIds !== null) onSaved?.();
      toast.error(message);
    } finally {
      if (isCurrent()) {
        saveRequestRef.current = null;
        setSaving(false);
        busyCallbackRef.current?.(false);
      }
    }
  };

  const STEPS = [
    t('Contenedor'),
    t('Productos vendidos'),
    t('Venta'),
    t('Comentario y foto'),
    t('Revisar y guardar'),
  ];
  const visibleSteps = STEPS.slice(firstStep);
  const visibleStep = step - firstStep;
  const isLastStep = step === STEPS.length - 1;

  const canGoNext =
    !loadingProducts &&
    !productsError &&
    (step === 0
      ? !!shipment || withoutContainer
      : step === 1
        ? hasChanges
        : step === 2
          ? !!saleDate
          : true);

  const goNext = () => {
    if (saveRequestRef.current) return;
    if (step === 1 && !validateProducts()) return;
    setShowErrors(false);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    if (!saveRequestRef.current) setStep((s) => Math.max(s - 1, firstStep));
  };

  const marketName = markets.find((m) => m.id === market)?.name;

  const renderContainerStep = () =>
    shipment || withoutContainer ? (
      <div className='flex items-start justify-between gap-3 rounded-xl border border-default-200 p-4'>
        <div className='min-w-0'>
          {shipment ? (
            <>
              <p className='font-mono text-lg font-semibold'>
                {shipmentLabel(shipment)}
              </p>
              <p className='text-default-600 truncate'>
                {t('Cliente')}: {shipment.receiver || '-'}
              </p>
              <p className='text-xs text-default-400'>
                {[
                  shipment.ship_name,
                  shipment.ship_eta && `ETA ${shipment.ship_eta}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </>
          ) : (
            <p className='text-default-600'>{t('Registro sin contenedor')}</p>
          )}
        </div>
        <Button size='sm' variant='light' onPress={changeContainer}>
          {t('Cambiar')}
        </Button>
      </div>
    ) : (
      <>
        <Input
          autoFocus
          size='lg'
          value={search}
          onValueChange={setSearch}
          placeholder={t('Contenedor o referencia, ej. FBIU56')}
          startContent={<Icon icon='tabler:search' width={20} />}
          autoCapitalize='characters'
          autoComplete='off'
          className='font-mono'
        />
        {searching && (
          <div
            data-loading-row
            aria-busy='true'
            className='mt-3 flex flex-col divide-y divide-default-100 rounded-xl border border-default-200'
          >
            {[0, 1].map((i) => (
              <div key={i} className='flex flex-col gap-2 p-4 animate-pulse'>
                <div className='h-4 w-40 rounded-md bg-default-200' />
                <div className='h-3 w-24 rounded-md bg-default-100' />
              </div>
            ))}
          </div>
        )}
        {!searching && results.length > 0 && (
          <div className='mt-3 flex flex-col divide-y divide-default-100 rounded-xl border border-default-200'>
            {results.map((option) => (
              <button
                key={option.id}
                type='button'
                onClick={() => selectShipment(option)}
                className='flex items-center justify-between gap-3 p-4 text-left hover:bg-default-50 active:bg-default-100'
              >
                <div className='min-w-0'>
                  <p className='font-mono font-semibold'>
                    {shipmentLabel(option)}
                  </p>
                  <p className='text-sm text-default-500 truncate'>
                    {[
                      matchedByReference(option) &&
                        `${t('Ref.')} ${option.reference_number}`,
                      option.receiver || '-',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <Icon
                  icon='tabler:chevron-right'
                  className='shrink-0 text-default-400'
                />
              </button>
            ))}
          </div>
        )}
        {!searching &&
          results.length === 0 &&
          searchedTerm !== '' &&
          searchedTerm === search.trim() && (
            <p className='mt-3 text-sm text-default-400'>
              {t('No hay contenedores ni referencias que empiecen así')}
            </p>
          )}
        <Button
          variant='light'
          className='mt-3 px-0 text-default-500'
          isDisabled={!canLogWithoutContainer}
          onPress={chooseWithoutContainer}
        >
          {t('Registrar sin contenedor')}
        </Button>
        {!canLogWithoutContainer && (
          <p className='text-xs text-default-500'>
            {t(
              'Para registrar sin contenedor necesitas el permiso de Bitácora. Pídeselo al administrador de tu exportadora.'
            )}
          </p>
        )}
      </>
    );

  const destination = shipment
    ? [shipment.ship_destination_port, shipment.ship_destination_country]
        .filter(Boolean)
        .join(', ')
    : '';

  const renderMarketAndCurrency = () => (
    <div className='mb-3 flex gap-2'>
      <Autocomplete
        size='sm'
        label={t('Mercado')}
        selectedKey={market || null}
        onSelectionChange={(key) => chooseMarket(key ? String(key) : '')}
        description={
          marketSource === 'tracking'
            ? t('Según el tracking del contenedor')
            : marketSource === 'port'
              ? `${t('Según el puerto de descarga')}: ${destination}`
              : undefined
        }
        className='flex-1'
      >
        {marketOptions.map((m) => (
          <AutocompleteItem
            key={m.id}
            value={m.id}
            textValue={`${m.name} · ${m.country}`}
          >
            {`${m.name} · ${m.country}`}
          </AutocompleteItem>
        ))}
      </Autocomplete>
      <Select
        size='sm'
        label={t('Moneda')}
        selectedKeys={[currency]}
        onSelectionChange={(keys) =>
          setCurrency(String(Array.from(keys)[0] ?? currency))
        }
        className='w-32'
      >
        {currencyOptions.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </Select>
    </div>
  );

  // Nivel de detalle: con contenedor agrupa las cajas; sin contenedor define
  // qué campos se piden por producto.
  const renderDimensionChips = (
    options: Dimension[],
    active: Dimension[],
    onToggle: (dimension: Dimension) => void,
    locked: Dimension[] = []
  ) => (
    <>
      <p className='mb-1.5 text-xs font-medium text-default-500'>
        {t('Abrir por')}
      </p>
      <div className='mb-4 flex flex-wrap gap-1.5'>
        {options.map((dimension) => {
          const isActive = active.includes(dimension);
          return (
            <button
              key={dimension}
              type='button'
              aria-pressed={isActive}
              aria-disabled={locked.includes(dimension) || undefined}
              onClick={() => onToggle(dimension)}
              className={`rounded-full border px-3 py-1 text-sm ${
                isActive
                  ? 'border-primary bg-primary text-white'
                  : 'border-default-300 text-default-600'
              }`}
            >
              {DIMENSION_LABELS[dimension]}
            </button>
          );
        })}
      </div>
    </>
  );

  const renderContainerProducts = () => (
    <>
      <p className='mb-2 text-sm text-default-500'>
        {shipment && shipmentLabel(shipment)} ·{' '}
        {t('Precio por bulto de lo que se vendió.')}
      </p>
      {renderMarketAndCurrency()}
      {productsError && (
        <Alert variant='danger' showIcon className='mb-3'>
          <p>{t('No se pudieron cargar los productos del contenedor')}</p>
          <Button
            size='sm'
            variant='light'
            onPress={() => {
              if (shipment) void selectShipment(shipment);
            }}
          >
            {t('Reintentar')}
          </Button>
        </Alert>
      )}

      {!loadingProducts && boxesWithoutFormat > 0 && (
        <Alert variant='warning' showIcon className='mb-3'>
          <div className='flex flex-col gap-2'>
            <p className='font-semibold'>
              {t('Códigos de embalaje sin formato')}
            </p>
            <p>
              {boxesWithoutFormat.toLocaleString('es-CL')}{' '}
              {t(
                'bultos sin formato: faltan sus códigos de embalaje en el Mapeo de Códigos de Embalaje.'
              )}
            </p>
            {!showFormatMapping && codesWithoutFormat.length > 0 && (
              <p className='break-words'>{codesWithoutFormat.join(', ')}</p>
            )}
            <div className='flex flex-wrap gap-2'>
              <Button
                size='sm'
                variant='flat'
                onPress={() => setShowFormatMapping((current) => !current)}
                isDisabled={!exporterId || codesWithoutFormat.length === 0}
              >
                {showFormatMapping ? t('Ocultar mapeo') : t('Abrir mapeo')}
              </Button>
            </div>
            {showFormatMapping && exporterId && shipment && (
              <PackingCodeMappingEditor
                key={`${exporterId}:${shipment.id}`}
                exporterId={exporterId}
                codes={codesWithoutFormat}
                onMapped={applyFormatMapping}
              />
            )}
          </div>
        </Alert>
      )}

      {renderDimensionChips(visibleDimensions, dimensions, toggleDimension)}

      {loadingProducts ? (
        <div className='flex justify-center p-6'>
          <Spinner />
        </div>
      ) : (
        <div className='flex flex-col gap-2.5'>
          {groups.map((group) => {
            const error =
              showErrors && group.edited && group.price
                ? priceError(group.price)
                : undefined;
            const title =
              group.parts.filter(Boolean).join(' · ') ||
              t('Todo el contenedor');
            return (
              <div
                key={group.key}
                data-row-key={group.key}
                className='rounded-xl border border-default-200 p-3'
              >
                <div className='flex items-center justify-between gap-3'>
                  <div className='min-w-0'>
                    <p className='font-semibold leading-tight'>{title}</p>
                    <p className='mt-0.5 text-xs text-default-400'>
                      {group.quantity.toLocaleString('es-CL')} {t('bultos')}
                      {!dimensions.includes('pallet') && group.pallets > 0
                        ? ` · ${group.pallets} ${group.pallets === 1 ? t('folio') : t('folios')}`
                        : ''}
                    </p>
                    {group.range && (
                      <p className='mt-0.5 text-xs text-default-500'>
                        {t('Varios precios')}: {group.range} {currency}
                      </p>
                    )}
                    {group.reportedSome && !group.edited && (
                      <p className='mt-0.5 text-xs text-success-600'>
                        {group.reportedAll
                          ? `${t('Informado')}${group.reportedDate ? ` ${group.reportedDate}` : ''}`
                          : t('Informado en parte')}
                      </p>
                    )}
                  </div>
                  <Input
                    size='sm'
                    aria-label={t('Precio por bulto')}
                    placeholder={
                      group.price === null ? t('Varios') : t('Precio')
                    }
                    inputMode='decimal'
                    value={group.price ?? ''}
                    onValueChange={(v) => setGroupPrice(group.boxIds, v)}
                    endContent={
                      <span className='text-xs text-default-400'>
                        {currency}
                      </span>
                    }
                    isInvalid={!!error}
                    errorMessage={error}
                    className='w-32 shrink-0'
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const renderManualField = (
    row: ManualRow,
    dimension: Dimension,
    errors: Partial<Record<keyof ManualRow, string>>
  ) => {
    switch (dimension) {
      case 'variety':
      case 'caliber': {
        const options = dimension === 'variety' ? varieties : calibers;
        return (
          <Select
            key={dimension}
            size='sm'
            label={DIMENSION_LABELS[dimension]}
            selectedKeys={row[dimension] ? [row[dimension]] : []}
            onSelectionChange={(keys) =>
              updateManualRow(row.key, {
                [dimension]: String(Array.from(keys)[0] ?? ''),
              })
            }
            isInvalid={!!errors[dimension]}
          >
            {options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </Select>
        );
      }
      case 'weight':
        return (
          <Input
            key={dimension}
            size='sm'
            label={t('Peso bulto')}
            inputMode='decimal'
            value={row.weight}
            onValueChange={(v) => updateManualRow(row.key, { weight: v })}
            endContent={<span className='text-xs text-default-400'>kg</span>}
            isInvalid={!!errors.weight}
          />
        );
      case 'format':
      case 'color':
      case 'label':
      case 'category':
        return (
          <Input
            key={dimension}
            size='sm'
            label={DIMENSION_LABELS[dimension]}
            value={row[dimension]}
            onValueChange={(v) => updateManualRow(row.key, { [dimension]: v })}
          />
        );
      default:
        return null;
    }
  };

  const renderManualProducts = () => (
    <>
      <p className='mb-3 text-sm text-default-500'>
        {t('Variedad, calibre y peso son obligatorios.')}
      </p>
      {renderMarketAndCurrency()}
      {renderDimensionChips(
        MANUAL_DIMENSIONS,
        manualDimensions,
        toggleManualDimension,
        REQUIRED_MANUAL_DIMENSIONS
      )}
      <div className='flex flex-col gap-3'>
        {manualRows.map((row) => {
          const errors = showErrors ? manualRowErrors(row) : {};
          return (
            <div
              key={row.key}
              data-row-key={row.key}
              className='rounded-xl border border-default-200 p-3'
            >
              <div className='mb-2 flex items-center justify-between'>
                <p className='font-semibold text-default-600'>
                  {t('Producto')}
                </p>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  aria-label={t('Quitar producto')}
                  onPress={() => removeManualRow(row.key)}
                >
                  <Icon icon='tabler:x' width={16} />
                </Button>
              </div>
              <div className='grid grid-cols-2 gap-2'>
                {manualDimensions.map((dimension) =>
                  renderManualField(row, dimension, errors)
                )}
                <Input
                  size='sm'
                  label={t('Precio por bulto')}
                  inputMode='decimal'
                  value={row.price}
                  onValueChange={(v) => updateManualRow(row.key, { price: v })}
                  endContent={
                    <span className='text-xs text-default-400'>{currency}</span>
                  }
                  isInvalid={!!errors.price}
                />
              </div>
              {showErrors &&
                (errors.variety ||
                  errors.caliber ||
                  errors.weight ||
                  errors.price) && (
                  <p className='mt-1.5 text-xs text-danger'>
                    {errors.variety ||
                      errors.caliber ||
                      errors.weight ||
                      errors.price}
                  </p>
                )}
            </div>
          );
        })}
        <Button
          variant='flat'
          startContent={<Icon icon='tabler:plus' />}
          onPress={() => setManualRows((prev) => [...prev, emptyManualRow()])}
        >
          {t('Agregar producto')}
        </Button>
      </div>
    </>
  );

  const renderSaleStep = () => (
    <div className='flex flex-col gap-3'>
      <Input
        type='date'
        size='lg'
        label={t('Fecha de venta')}
        value={saleDate}
        max={today()}
        onValueChange={setSaleDate}
      />
      {daysFromEta !== null && (
        <p className='-mt-1 text-sm text-default-500'>
          {daysFromEta >= 0
            ? `${daysFromEta} ${daysFromEta === 1 ? t('día') : t('días')} ${t('desde la ETA')} (${shipment?.ship_eta})`
            : `${t('La fecha de venta es anterior a la ETA')} (${shipment?.ship_eta})`}
        </p>
      )}
    </div>
  );

  const renderCommentStep = () => (
    <LogbookNoteEditor
      note={comment}
      onNoteChange={setComment}
      photos={photos}
      onPhotosChange={setPhotos}
      disabled={draftLocked}
      optional
    />
  );

  const summaryRow = (label: string, value: ReactNode) => (
    <div className='flex justify-between gap-4 py-2'>
      <span className='text-sm text-default-500'>{label}</span>
      <span className='text-right text-sm font-medium'>{value}</span>
    </div>
  );

  // En el resumen: los grupos tocados en esta visita, al nivel en que se
  // informaron; sin contenedor, las filas con precio.
  const summaryItems = shipment
    ? groups
        .filter((g) => g.edited)
        .map((g) => ({
          key: g.key,
          title: g.parts.filter(Boolean).join(' · ') || t('Todo el contenedor'),
          detail: `${g.quantity.toLocaleString('es-CL')} ${t('bultos')}`,
          price: g.price || g.range,
        }))
    : pricedManualRows.map((r) => ({
        key: r.key,
        title: `${r.variety} · ${r.caliber} · ${formatWeight(toNumber(r.weight))}`,
        detail: null as string | null,
        price: r.price,
      }));

  const renderSummaryStep = () => (
    <div className='flex flex-col gap-4'>
      <div className='divide-y divide-default-100 rounded-xl border border-default-200 px-4'>
        {summaryRow(
          t('Contenedor'),
          shipment ? shipmentLabel(shipment) : t('Sin contenedor')
        )}
        {shipment && summaryRow(t('Cliente'), shipment.receiver || '-')}
        {summaryRow(t('Fecha de venta'), saleDate)}
        {daysFromEta !== null &&
          summaryRow(t('Días desde la ETA'), `${daysFromEta}`)}
        {summaryRow(t('Mercado'), marketName || '-')}
        {summaryRow(t('Registrado por'), userName || '-')}
      </div>

      <div className='flex flex-col gap-2'>
        {summaryItems.map((item) => (
          <div
            key={item.key}
            className='flex items-center justify-between gap-3 rounded-xl bg-default-50 p-3'
          >
            <div className='min-w-0'>
              <p className='font-semibold leading-tight'>{item.title}</p>
              {item.detail && (
                <p className='text-xs text-default-500'>{item.detail}</p>
              )}
            </div>
            <p className='shrink-0 font-semibold'>
              {item.price ? `${item.price} ${currency}` : t('Sin precio')}
            </p>
          </div>
        ))}
      </div>

      {(comment.trim() || photos.length > 0) && (
        <div className='rounded-xl border border-default-200 p-3'>
          {comment.trim() && <p className='text-sm'>{comment.trim()}</p>}
          {photos.length > 0 && (
            <p className='mt-1 text-xs text-default-400'>
              {photos.length} {photos.length === 1 ? t('foto') : t('fotos')}
            </p>
          )}
        </div>
      )}
    </div>
  );

  const stepContent = [
    renderContainerStep,
    shipment ? renderContainerProducts : renderManualProducts,
    renderSaleStep,
    renderCommentStep,
    renderSummaryStep,
  ][step];

  const progress = (
    <>
      <div className='flex items-center gap-2 pr-6'>
        <span className='text-base font-semibold'>
          {t('Registrar precios')}
        </span>
        <span className='text-sm font-normal text-default-400'>
          {visibleStep + 1}/{visibleSteps.length}
        </span>
      </div>
      <div className='flex gap-1'>
        {visibleSteps.map((_, i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full ${i <= visibleStep ? 'bg-primary' : 'bg-default-200'}`}
          />
        ))}
      </div>
      <span className='text-xl font-semibold'>{STEPS[step]}</span>
    </>
  );
  const navigation = (
    <div className='flex w-full gap-2'>
      {step > firstStep ? (
        <Button
          variant='flat'
          onPress={capturePending ? handleClose : goBack}
          isDisabled={saving}
          className='flex-1'
        >
          {t(capturePending ? 'Cerrar' : 'Atrás')}
        </Button>
      ) : (
        <Button variant='light' onPress={handleClose} className='flex-1'>
          {t('Cancelar')}
        </Button>
      )}
      {isLastStep ? (
        <Button
          color='primary'
          onPress={handleSave}
          isLoading={saving}
          className='flex-1'
        >
          {t(
            capturePending
              ? captureIntentRef.current?.commentIds != null
                ? 'Reintentar fotos'
                : 'Reintentar guardado'
              : 'Guardar'
          )}
        </Button>
      ) : (
        <Button
          color='primary'
          onPress={goNext}
          isDisabled={saving || !canGoNext}
          className='flex-1'
        >
          {t('Siguiente')}
        </Button>
      )}
    </div>
  );
  if (presentation === 'embedded') {
    return (
      <section
        aria-label={t('Registrar precios')}
        className='mx-auto flex w-full max-w-2xl flex-col gap-4 pt-4'
      >
        <div className='flex flex-col gap-3'>{progress}</div>
        {captureError && (
          <Alert variant='warning' showIcon>
            {captureError}
          </Alert>
        )}
        <fieldset disabled={draftLocked} className='min-w-0 pb-4'>
          {stepContent()}
        </fieldset>
        <div className='sticky bottom-0 border-t border-default-100 bg-content1 py-3'>
          {navigation}
        </div>
      </section>
    );
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isDismissable={!saving}
      isKeyboardDismissDisabled={saving}
      hideCloseButton={saving}
      size={isSmallScreen ? 'full' : 'lg'}
      scrollBehavior='inside'
      placement='center'
      classNames={{
        wrapper: 'z-[10000]',
        backdrop: 'z-[10000]',
        base: isSmallScreen ? 'max-h-[100dvh]' : undefined,
      }}
    >
      <ModalContent>
        <ModalHeader className='flex flex-col gap-3 pb-2'>
          {progress}
        </ModalHeader>
        <ModalBody className='pb-4'>
          {captureError && (
            <Alert variant='warning' showIcon>
              {captureError}
            </Alert>
          )}
          <fieldset disabled={draftLocked} className='min-w-0'>
            {stepContent()}
          </fieldset>
        </ModalBody>
        <ModalFooter className='border-t border-default-100'>
          {navigation}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
